<script lang="ts" setup>
  import { isIOS } from '@vueuse/core'
  import type { AssetStoryblok, MobileAppBannerStoryblok, MultilinkStoryblok } from '@/storyblok/component-types-sb'
  import useIndexPageDataLayer from '@/composables/data-layers/useIndexPageDataLayer'
  import { toAssetImageProps, toMultilinkHref } from '@/utils/storyblok/normalizers'

  const props = defineProps<{
    blok: MobileAppBannerStoryblok
  }>()

  const { isDesktop } = useBTBreakpoints()

  const descriptionText = computed(() => renderRichText(props.blok.description))

  const { pushAppPromoBannerDataLayer } = useIndexPageDataLayer()

  interface IStoreLink {
    href?: MultilinkStoryblok
    image: AssetStoryblok
    label: string
  }

  const onStoreLinkClick = async (store: IStoreLink) => {
    if (!store.href) return

    pushAppPromoBannerDataLayer({
      event_label: store.label,
      event_category: 'click'
    })

    await navigateTo(store.href.url, {
      external: true,
      open: { target: store.href.target ?? '_self' }
    })
  }

  const isMounted = useMounted()

  const storeLinks = computed(() => {
    const stores: IStoreLink[] = []

    if (props.blok.showAppleStore && props.blok.appleStore && (isDesktop.value || !isMounted.value || isIOS)) {
      stores.push({
        href: props.blok.appleStoreHref,
        image: props.blok.appleStore,
        label: 'app_store'
      })
    }

    if (props.blok.showPlayStore && props.blok.playStore && (isDesktop.value || !isMounted.value || !isIOS)) {
      stores.push({
        href: props.blok.playStoreHref,
        image: props.blok.playStore,
        label: 'google_play'
      })
    }

    return stores
  })

  const storeImageProps = (store: IStoreLink) => toAssetImageProps(store.image)
  const qrImageProps = computed(() => toAssetImageProps(props.blok.qrCode))

  const banner = ref<HTMLElement | null>(null)

  onElementVisibleAtThreshold(banner, () =>
    pushAppPromoBannerDataLayer({
      event_category: 'impressions'
    })
  )

  const preTextId = `pre-text-${props.blok._uid}`
</script>

<template>
  <div
    ref="banner"
    class="flex h-130 flex-col items-center px-4 pt-14 sm:h-120 sm:pt-9 md:h-auto md:w-130 md:items-start md:pb-14 md:pl-12 md:pt-16 lg:w-166"
  >
    <h2 class="m-0 pb-7 text-center md:pb-4 md:text-left">{{ blok.title }}</h2>
    <div v-if="isDesktop">
      <div
        class="pb-8 text-body-1"
        v-html="descriptionText"
      />
      <div
        :id="preTextId"
        class="text-subtitle-1"
      >
        {{ blok.preText }}
      </div>
    </div>
    <div
      class="flex h-10 items-center gap-2 md:h-20 md:gap-6"
      :aria-describedby="preTextId"
    >
      <NuxtLink
        v-for="store in storeLinks"
        :key="store.image.filename ?? store.image.id"
        :to="toMultilinkHref(store.href)"
        @click.prevent="onStoreLinkClick(store)"
      >
        <NuxtImg
          :src="storeImageProps(store).src"
          :alt="storeImageProps(store).alt"
          provider="storyblok"
          class="h-10 w-full"
          width="auto"
          height="40"
        />
      </NuxtLink>
      <NuxtImg
        v-if="isDesktop && blok.showQrCode"
        :src="qrImageProps.src"
        :alt="qrImageProps.alt"
        provider="storyblok"
        class="size-20"
        width="80"
        height="80"
      />
    </div>
  </div>
</template>
